import { SWRFetcher } from '@commenda/utils';
import useSWR, { KeyedMutator } from 'swr';
import { formatArrayToString } from '../utils';
import {
  CustomerExemptionResponse,
  CustomerExtend,
  CustomerGetResponse,
  ExemptionFiltersInterface,
} from './types';

export function useCustomer({
  customerId,
  corporation_id,
}: {
  customerId: string;
  corporation_id: number;
}) {
  const query = new URLSearchParams();

  query.append('extend', CustomerExtend.SHIPPING_ADDRESS);
  query.append('corporation_id', corporation_id.toString());

  const {
    data,
    mutate,
    error,
  }: {
    data?: CustomerGetResponse;
    error?: unknown;
    mutate?: KeyedMutator<unknown>;
  } = useSWR(
    customerId ? `new-sales-tax/customers/${customerId}?${query}` : null,
    SWRFetcher(
      {
        method: 'GET',
      },
      undefined
    )
  );

  return {
    customer: data?.data,
    mutate,
    error,
  };
}

interface GetCustomerExemptionsParams {
  cursor?: string | null;
  limit?: number;
  filters: ExemptionFiltersInterface & { customerId: string };
}

export function useCustomerExemptions({
  cursor = null,
  limit = 10,
  filters,
}: GetCustomerExemptionsParams) {
  const query = new URLSearchParams();

  query.append('limit', limit.toString());

  if (filters.corporationId) {
    query.append('corporation_id', filters.corporationId.toString());
  }

  if (cursor) {
    query.append('cursor', cursor);
  }

  if (filters.certificateTypes) {
    const certificateTypesFilter = formatArrayToString(
      filters.certificateTypes
    );
    query.append('certificate_types', certificateTypesFilter);
  }

  if (filters.exemptionJurisdiction) {
    const exemptionJurisdictionFilter = formatArrayToString(
      filters.exemptionJurisdiction
    );
    query.append('exemption_jurisdiction', exemptionJurisdictionFilter);
  }

  if (filters.exemptionStatus) {
    query.append('exemption_status', filters.exemptionStatus);
  }

  if (filters.expirationStatus) {
    query.append('expiration_status', filters.expirationStatus);
  }

  const {
    data,
    mutate,
    error,
    isLoading,
  }: {
    data?: CustomerExemptionResponse;
    error?: unknown;
    mutate?: KeyedMutator<unknown>;
    isLoading: boolean;
  } = useSWR(
    `new-sales-tax/customers/${filters.customerId}/exemptions?${query}`,
    SWRFetcher(
      {
        method: 'GET',
      },
      undefined
    )
  );

  return {
    exemptions: data?.data.exemptions,
    cursor: data?.data.cursor,
    totalExemptions: data?.data.total_exemptions,
    isLoading,
    mutate,
    error,
  };
}
